<template>
  <div class="signin" data-testid="signin">
    <img src="../assets/logo.png" class="mb-4" width="140">
    <v-alert
      class="signin__error text-left"
      color="red"
      type="error"
      v-if="error"
      data-testid="signin__error"
    >
      {{error}}
    </v-alert>
    <p v-else data-testid="signin__message">{{ $t('label.enter_login') }}</p>
    <v-form v-model="valid">
      <v-text-field
        class="signin__email"
        :placeholder="$t('field.email')"
        v-model="model.email"
        hide-details
        @keyup.enter="signIn"
        data-testid="signin__email"
      />
      <v-text-field
        class="signin__password"
        :placeholder="$t('field.password')"
        v-model="model.password"
        hide-details
        type="password"
        @keyup.enter="signIn"
        data-testid="signin__password"
      />
      <v-checkbox
        class="mt-8 mb-3"
        :label="$t('field.keep')"
        v-model="model.keep"
        hide-details
        color="primary"
        data-testid="signin__keep"
      />
      <v-btn
        class="signin__submit mb-5"
        color="primary"
        large
        block
        :disabled="authenticating"
        :loading="authenticating"
        @click="signIn"
        data-testid="signin__submit"
      >
        {{ $t('button.login') }}
        <v-icon dark right>fa-arrow-circle-right</v-icon>
      </v-btn>
    </v-form>
    <v-btn text to="/forgot_password" class="forgot mb-5" data-testid="signin__forgot">
      <v-icon left>sentiment_very_dissatisfied</v-icon>
      {{ $t('label.forgot_password') }}
    </v-btn>
  </div>
</template>

<i18n lang="yaml">
ja:
  label:
    enter_login: 'ログイン情報を入力してください'
    forgot_password: 'パスワードを忘れた方はこちら'

  error:
    invalid_email_password: 'メールアドレスもしくはパスワードが間違っています'
    deactivated_user: 'このアカウントは無効になっています。管理者にお問い合せください。'
    blocked: 'ログインの失敗回数が制限を超えたため、アカウントはブロックされました。管理者にお問い合せください。'
    email_missing: 'メールアドレスを入力してください。'
    password_missing: 'パスワードを入力してください。'
    email_unverified: 'メールアドレスの確認が完了していません。確認用メールの指示に従ってください。'
    universal_login_failed: 'Auth0 でのログインに失敗しました。もう一度お試しください。'

  field:
    email: 'E-mail'
    password: 'パスワード'
    keep: 'ログイン状態を保存する'

  button:
    login: 'ログイン'

en:
  label:
    enter_login: 'Enter your login information'
    forgot_password: 'Forgot your password?'

  error:
    invalid_email_password: 'Wrong email or password.'
    deactivated_user: 'This user is deactivated. Please contact an administrator.'
    blocked: 'Your account has been blocked after too many failed login attempts. Please contact an administrator.'
    email_missing: 'Please enter your email address.'
    password_missing: 'Please enter your password.'
    email_unverified: 'Your mail address must be verified. Please follow the instructions in the verification email.'
    universal_login_failed: 'Failed to log in with Auth0. Please try again.'

  field:
    email: 'E-mail'
    password: 'Password'
    keep: 'Keep me logged in'

  button:
    login: 'Log in'
</i18n>

<script>
import { isUniversalLoginFailed } from '@/libs/universalLogin';

export default {
  name: 'signin',
  data() {
    return {
      authenticating: false,
      error: '',
      model: {
        email: '',
        password: '',
        keep: false,
      },
      valid: false,
    };
  },
  created() {
    if (isUniversalLoginFailed(this.$route.query)) {
      this.error = this.$t('error.universal_login_failed');
    }
  },
  methods: {
    signIn: async function () {
      if (!this.model.email) {
        this.error = this.$t('error.email_missing');
        return;
      }
      if (!this.model.password) {
        this.error = this.$t('error.password_missing');
        return;
      }
      this.authenticating = true;
      try {
        const response = await this.$http.post('/api/signin', {
          email: this.model.email,
          password: this.model.password,
          enable_otp: true,
        });

        if (response.data.otp_required) {
          // OTP-enabled user: redirect to OTP verification screen
          this.$router.push({
            path: '/verify_otp',
            query: { email: this.model.email },
          });
        } else {
          // Traditional authentication: store token and go back to the page the user wanted
          await this.$store.dispatch('setToken', {
            token: response.data.access_token,
            keep: this.model.keep,
          });
          this.$router.push(this.$route.query.redirectTo || '/');
        }
      } catch (error) {
        this.onLoginError(error);
      }
      this.authenticating = false;
    },
    onLoginError(error) {
      const errorCode = this._.get(error, 'response.data.error');

      if (['invalid_email_password', 'blocked', 'email_unverified', 'deactivated_user'].includes(errorCode)) {
        this.error = this.$t(`error.${errorCode}`);
      } else {
        this.error = error.message;
      }
    },
  },
};
</script>

<style scoped lang="sass">
.signin
  ::v-deep .v-text-field__slot input
    padding: 1.5rem
</style>
